/**
 * Paleta e formatadores compartilhados dos gráficos (at-86).
 *
 * ⚠️ POR QUE ESTE ARQUIVO EXISTE, EM VEZ DE REUSAR O `COLORS` DO CONTAC.
 *
 * O `DashboardCategoria.tsx` declara, dentro do componente, uma lista de 13 cores usada com
 * `COLORS[i % COLORS.length]` — ou seja, ciclada. Medida contra os critérios de acessibilidade de
 * gráfico (validador de paleta, 27/08/2026, superfície branca), as 8 primeiras REPROVAM em dois
 * pontos: faixa de luminosidade (`#004A8D` escura demais, `#00CAFF` e `#00FF63` claras demais,
 * `black` fora de qualquer faixa) e piso de croma (`black` não carrega identidade nenhuma — lê
 * como cinza). A lista ainda tem pares quase idênticos (`#00CAFF` × `#00D3FF`, `#004A8D` ×
 * `#0047FF`), que é o que a ciclagem garante colocar lado a lado quando há muitas séries.
 *
 * Copiar aquilo para a tela nova propagaria o defeito; corrigir a paleta do Contac repintaria
 * telas que ninguém pediu para mexer nesta entrega (e cor de série é memória do usuário). Então a
 * regra "segunda tela que precisa = extrair" foi cumprida extraindo um módulo NOVO e correto, com
 * a paleta validada, para o Contac migrar quando for tarefa dele. Item registrado no backlog.
 *
 * ⚠️ A ORDEM DOS SLOTS É O MECANISMO DE SEGURANÇA, não estética: ela foi escolhida para que
 * qualquer par ADJACENTE continue distinguível sob daltonismo (protanopia/deuteranopia). Trocar a
 * ordem, inserir uma cor no meio ou gerar uma 9ª cor quebra a garantia. Passando de 8 séries, o
 * caminho é agrupar o excedente em "Outras" ou virar tabela — nunca inventar cor.
 *
 * ⚠️ Só existe a versão CLARA: a aplicação inteira é de fundo branco, sem alternância de tema.
 * Se um dia houver tema escuro, estes valores precisam de um conjunto próprio, escolhido contra o
 * fundo escuro — não serve inverter.
 */

/**
 * Cores de SÉRIE (identidade: "qual série é esta").
 *
 * Atribuir sempre em ordem, do slot 1 em diante, e amarrar a cor à ENTIDADE, não à posição no
 * ranking — senão filtrar uma série repinta as demais e quem aprendeu "azul é X" é enganado.
 */
export const CORES_SERIE = [
  '#2a78d6', // 1 - azul
  '#eb6834', // 2 - laranja
  '#1baf7a', // 3 - verde-água
  '#eda100', // 4 - amarelo
  '#e87ba4', // 5 - magenta
  '#008300', // 6 - verde
  '#4a3aa7', // 7 - violeta
  '#e34948', // 8 - vermelho
] as const;

/**
 * Rampa ORDINAL (posição numa sequência: faixas de idade, faixas etárias, níveis).
 *
 * Uma cor só, do claro ao escuro, para o leitor VER a ordem na cor. São exatamente 5 degraus: com
 * 6, o degrau mais claro deixa de se distinguir do fundo branco (medido: 1,79:1, abaixo do piso de
 * 2:1). Precisando de mais faixas, agrupe faixas — não clareie a rampa.
 *
 * ⚠️ Não usar isto para categoria SEM ordem natural (marca, tipo, consultor): colorir por valor
 * gasta o canal de cor repetindo o que o tamanho da barra já diz.
 */
export const RAMPA_ORDINAL = ['#86b6ef', '#5598e7', '#2a78d6', '#1c5cab', '#104281'] as const;

/** Tinta do gráfico. Eixo e grade ficam recessivos: o dado é a única coisa que pode ser forte. */
export const TINTA_GRAFICO = {
  /** Categoria fora da ordem ("Não informado") e séries de contexto. */
  neutro: '#898781',
  grade: '#e1e0d9',
  eixo: '#c3c2b7',
  rotulo: '#52514e',
  superficie: '#ffffff',
} as const;

/**
 * Cor da série pelo índice, SEM ciclagem.
 *
 * Estourar os 8 slots é erro de desenho do gráfico, não do dado — a função devolve o neutro em vez
 * de reciclar uma cor, para o defeito aparecer como uma barra cinza (visível) em vez de duas
 * séries com a mesma cor (invisível).
 */
export const corDaSerie = (indice: number): string =>
  CORES_SERIE[indice] ?? TINTA_GRAFICO.neutro;

/** Degrau da rampa ordinal, com o neutro reservado para o balde "sem informação". */
export const corOrdinal = (indice: number, total: number): string => {
  if (indice < 0 || total <= 0) return TINTA_GRAFICO.neutro;
  const passo = Math.min(
    RAMPA_ORDINAL.length - 1,
    Math.round((indice / Math.max(1, total - 1)) * (RAMPA_ORDINAL.length - 1)),
  );
  return RAMPA_ORDINAL[passo];
};

export const formatarMoeda = (valor: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor ?? 0);

/** Para eixo e rótulo dentro do gráfico, onde o valor cheio não cabe (R$ 58,4 mi). */
export const formatarMoedaCompacta = (valor: number): string => {
  const v = valor ?? 0;
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `R$ ${(v / 1_000_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} bi`;
  if (abs >= 1_000_000) return `R$ ${(v / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`;
  if (abs >= 1_000) return `R$ ${(v / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`;
  return formatarMoeda(v);
};

export const formatarNumero = (valor: number): string =>
  (valor ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });

export const formatarPercentual = (valor: number): string =>
  `${(valor ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
